/**
 * BookingEmailsTab Component
 *
 * Displays email delivery history for a booking and provides
 * buttons to send different types of emails.
 */

import { useState, useEffect } from 'react';
import {
  Mail,
  Lock,
  Truck,
  XCircle,
  ChevronDown,
  ChevronUp,
  Loader2,
  AlertCircle,
  Clock,
  CheckCircle,
  Send,
  Eye,
  MousePointer,
  AlertTriangle,
  RefreshCw,
  Link2,
  Copy,
  Check
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import {
  getEmailDeliveryLogs,
  getEmailStatusBadgeClass,
  formatEmailStatus,
  getEmailTypeName,
  type EmailDeliveryLog,
} from '../services/emailDeliveryLog.service';
import { generateManageLink } from '../services/manageLink.service';
import { useToast } from '../contexts/ToastContext';
import { useDateFormatter } from '../hooks/useDateFormatter';

interface BookingEmailsTabProps {
  bookingId: string;
  bookingState: string;
  paymentState: string;
  language: string;
  selfServiceEnabled: boolean;
  isDeliveryLocation: boolean;
  twoEmailsEnabled: boolean;
  endDate: string;
}

// Booking webhook endpoint (generates token + forwards to Make.com)
const BOOKING_WEBHOOK_URL = '/api/booking-webhook';

// Icon mapping for email types
const emailTypeIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  Mail,
  Lock,
  Truck,
  XCircle,
  RefreshCw,
};

// Icon mapping for events
const eventIcons: Record<string, React.ComponentType<{ className?: string }>> = {
  queued: Clock,
  sent: Send,
  delivered: CheckCircle,
  opened: Eye,
  clicked: MousePointer,
  bounced: AlertTriangle,
  failed: XCircle,
  dropped: AlertTriangle,
};

export default function BookingEmailsTab({
  bookingId,
  bookingState,
  paymentState,
  language,
  selfServiceEnabled,
  isDeliveryLocation,
  twoEmailsEnabled,
  endDate,
}: BookingEmailsTabProps) {
  const { t } = useTranslation();
  const { showToast } = useToast();
  const { formatDateTimeCompact, formatTime } = useDateFormatter();
  const [emailLogs, setEmailLogs] = useState<EmailDeliveryLog[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expandedLogs, setExpandedLogs] = useState<Set<string>>(new Set());
  const [sendingEmail, setSendingEmail] = useState<string | null>(null);

  // Manage-booking link (operator copies & shares it themselves)
  const [manageLink, setManageLink] = useState<{ url: string; expiresAt: string } | null>(null);
  const [generatingLink, setGeneratingLink] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);

  useEffect(() => {
    loadEmailLogs();
  }, [bookingId]);

  async function loadEmailLogs() {
    setLoading(true);
    setError(null);

    const { data, error: loadError } = await getEmailDeliveryLogs(bookingId);

    if (loadError) {
      setError(loadError);
    } else {
      setEmailLogs(data || []);
    }

    setLoading(false);
  }

  const toggleExpanded = (logId: string) => {
    setExpandedLogs(prev => {
      const newSet = new Set(prev);
      if (newSet.has(logId)) {
        newSet.delete(logId);
      } else {
        newSet.add(logId);
      }
      return newSet;
    });
  };

  const sendEmail = async (emailType: 'initial' | 'codes' | 'deliveries') => {
    setSendingEmail(emailType);

    const payload = {
      booking_id: bookingId,
      end_date: endDate,
      send_now: emailType !== 'initial', // true for codes and deliveries, false for initial
      booking_state: bookingState,
      payment_state: paymentState,
      language: language,
      self_service_enabled: selfServiceEnabled,
      deliveries: isDeliveryLocation,
    };

    try {
      const response = await fetch(BOOKING_WEBHOOK_URL, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Failed to send email: ${response.statusText}`);
      }

      showToast(t('bookingEmails.emailRequestSent'), 'success');

      // Reload email logs after a short delay to show updated status
      setTimeout(() => {
        loadEmailLogs();
      }, 2000);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : t('bookingEmails.failedToSendEmail');
      showToast(errorMessage, 'error');
    } finally {
      setSendingEmail(null);
    }
  };

  const copyManageLink = async (url: string) => {
    try {
      await navigator.clipboard.writeText(url);
      setLinkCopied(true);
      showToast(t('bookingEmails.linkCopied'), 'success');
      setTimeout(() => setLinkCopied(false), 2000);
    } catch {
      // Clipboard API unavailable (e.g. insecure context) — the URL stays
      // visible on screen so the operator can select and copy it manually.
    }
  };

  const handleGenerateManageLink = async () => {
    setGeneratingLink(true);

    const { data, error } = await generateManageLink({ bookingId });

    if (error || !data) {
      showToast(error || t('bookingEmails.failedToGenerateLink'), 'error');
    } else {
      setManageLink({ url: data.url, expiresAt: data.expiresAt });
      await copyManageLink(data.url);
    }

    setGeneratingLink(false);
  };

  const getIconComponent = (emailType: number): React.ComponentType<{ className?: string }> => {
    const iconNames: Record<number, string> = {
      1: 'Mail',
      2: 'Lock',
      3: 'Truck',
      4: 'XCircle',
    };
    return emailTypeIcons[iconNames[emailType]] || Mail;
  };

  const formatTimestamp = (timestamp: string): string => formatDateTimeCompact(timestamp);

  const formatEventTime = (timestamp: string): string => formatTime(timestamp);

  // Show buttons for non-delivery locations
  // Initial email only shown when self-service is disabled OR two-step email is enabled
  const showInitialEmailButton = !isDeliveryLocation && (!selfServiceEnabled || twoEmailsEnabled);
  const showCodesEmailButton = !isDeliveryLocation && selfServiceEnabled;
  // Show button for delivery locations only
  const showDeliveriesEmailButton = isDeliveryLocation;

  return (
    <div className="space-y-lg">
      {/* Manage-booking link — operator generates it and shares it themselves
          (own email / SMS / WhatsApp). Balance-independent; the manage app
          offers payment via Stripe and PayPal. */}
      <div className="p-md border border-border rounded-lg bg-secondary/5">
        <div className="flex items-start gap-md">
          <div className="w-10 h-10 rounded-full bg-blue/10 flex items-center justify-center flex-shrink-0">
            <Link2 className="w-5 h-5 text-blue" />
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-primary mb-xs">{t('bookingEmails.manageBookingLink')}</h3>
            <p className="text-sm text-secondary mb-md">{t('bookingEmails.manageBookingLinkDescription')}</p>

            {manageLink && (
              <div className="mb-md p-sm bg-primary/5 border border-primary/20 rounded-lg">
                <a
                  href={manageLink.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue hover:underline break-all"
                >
                  {manageLink.url}
                </a>
                <p className="text-xs text-secondary/70 mt-xs">
                  {t('bookingEmails.linkExpires', { date: formatTimestamp(manageLink.expiresAt) })}
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row flex-wrap gap-sm">
              <button
                onClick={handleGenerateManageLink}
                disabled={generatingLink}
                className="btn btn-secondary text-sm"
              >
                {generatingLink ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Link2 className="w-4 h-4" />
                )}
                {manageLink ? t('bookingEmails.regenerateLink') : t('bookingEmails.generateLink')}
              </button>

              {manageLink && (
                <button
                  onClick={() => copyManageLink(manageLink.url)}
                  className="btn btn-secondary text-sm"
                >
                  {linkCopied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {t('bookingEmails.copyLink')}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Send Email Buttons */}
      <div className="flex flex-col sm:flex-row flex-wrap gap-sm sm:gap-md">
        {showInitialEmailButton && (
          <button
            onClick={() => sendEmail('initial')}
            disabled={sendingEmail !== null}
            className="btn btn-secondary w-full sm:w-auto text-sm"
          >
            {sendingEmail === 'initial' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Mail className="w-4 h-4" />
            )}
            {t('bookingEmails.sendInitialEmail')}
          </button>
        )}

        {showCodesEmailButton && (
          <button
            onClick={() => sendEmail('codes')}
            disabled={sendingEmail !== null}
            className="btn btn-secondary w-full sm:w-auto text-sm"
          >
            {sendingEmail === 'codes' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Lock className="w-4 h-4" />
            )}
            {t('bookingEmails.sendEmailWithCodes')}
          </button>
        )}

        {showDeliveriesEmailButton && (
          <button
            onClick={() => sendEmail('deliveries')}
            disabled={sendingEmail !== null}
            className="btn btn-secondary w-full sm:w-auto text-sm"
          >
            {sendingEmail === 'deliveries' ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Truck className="w-4 h-4" />
            )}
            {t('bookingEmails.sendEmailForDeliveries')}
          </button>
        )}
      </div>

      {/* Email History Section */}
      <div className="space-y-md">
        <div className="flex items-center gap-md">
          <h3 className="text-lg font-semibold text-primary">{t('bookingEmails.emailHistory')}</h3>
          <button
            onClick={loadEmailLogs}
            disabled={loading}
            className="p-1 text-secondary hover:text-primary hover:bg-secondary rounded-full transition-colors"
            title={t('bookingEmails.refreshEmailHistory')}
          >
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-xl">
            <Loader2 className="w-6 h-6 text-blue animate-spin" />
            <span className="ml-md text-secondary">{t('bookingEmails.loadingEmailHistory')}</span>
          </div>
        ) : error ? (
          <div className="p-lg bg-danger/10 border border-danger/20 rounded-lg">
            <div className="flex items-center gap-md">
              <AlertCircle className="w-5 h-5 text-danger flex-shrink-0" />
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-danger mb-xs">{t('bookingEmails.errorLoadingEmailHistory')}</h4>
                <p className="text-sm text-danger/80">{error}</p>
              </div>
              <button
                onClick={loadEmailLogs}
                className="btn btn-ghost text-danger hover:bg-danger/10"
              >
                {t('common.retry')}
              </button>
            </div>
          </div>
        ) : emailLogs.length === 0 ? (
          <div className="text-center py-xl border border-border rounded-lg bg-secondary/20">
            <div className="w-12 h-12 rounded-full bg-tertiary mx-auto mb-md flex items-center justify-center">
              <Mail className="w-6 h-6 text-secondary" />
            </div>
            <p className="text-secondary text-sm">
              {t('bookingEmails.noEmailsSent')}
            </p>
          </div>
        ) : (
          <div className="space-y-md">
            {emailLogs.map((log) => {
              const IconComponent = getIconComponent(log.email_type);
              const isExpanded = expandedLogs.has(log.id);
              const events = (Array.isArray(log.events) ? log.events : []) as Array<Record<string, any>>;

              return (
                <div
                  key={log.id}
                  className="border border-border rounded-lg overflow-hidden"
                >
                  {/* Email Header */}
                  <div className="p-md bg-secondary/5">
                    <div className="flex items-start justify-between gap-md">
                      <div className="flex items-start gap-md">
                        <div className="w-10 h-10 rounded-full bg-blue/10 flex items-center justify-center flex-shrink-0">
                          <IconComponent className="w-5 h-5 text-blue" />
                        </div>
                        <div>
                          <div className="flex items-center gap-md mb-xs">
                            <h4 className="font-semibold text-primary">
                              {getEmailTypeName(log.email_type)}
                            </h4>
                            <span className={`badge ${getEmailStatusBadgeClass(log.current_status)}`}>
                              {formatEmailStatus(log.current_status)}
                            </span>
                          </div>
                          <p className="text-sm text-secondary">
                            {t('bookingEmails.sentTo')}: {log.current_email}
                          </p>
                          <p className="text-xs text-secondary/70 mt-xs">
                            {formatTimestamp(
                              events.length > 0
                                ? events[events.length - 1].timestamp
                                : log.updated_at || log.created_at
                            )}
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Events Toggle */}
                    {events.length > 0 && (
                      <button
                        onClick={() => toggleExpanded(log.id)}
                        className="flex items-center gap-xs text-sm text-blue hover:text-blue/80 mt-md transition-colors"
                      >
                        {isExpanded ? (
                          <ChevronUp className="w-4 h-4" />
                        ) : (
                          <ChevronDown className="w-4 h-4" />
                        )}
                        {isExpanded ? t('bookingEmails.hideEvents') : t('bookingEmails.showEvents')} ({events.length})
                      </button>
                    )}
                  </div>

                  {/* Events Timeline */}
                  {isExpanded && events.length > 0 && (
                    <div className="p-md border-t border-border bg-primary">
                      <div className="space-y-sm">
                        {events.map((event, idx) => {
                          const EventIcon = eventIcons[event.event.toLowerCase()] || Clock;
                          const isLast = idx === events.length - 1;

                          return (
                            <div key={idx} className="flex items-start gap-md">
                              <div className="flex flex-col items-center">
                                <div className="w-6 h-6 rounded-full bg-tertiary flex items-center justify-center">
                                  <EventIcon className="w-3 h-3 text-secondary" />
                                </div>
                                {!isLast && (
                                  <div className="w-px h-4 bg-border mt-xs" />
                                )}
                              </div>
                              <div className="flex-1 pb-sm">
                                <div className="flex items-center gap-md">
                                  <span className="text-sm font-medium text-primary capitalize">
                                    {event.event}
                                  </span>
                                  <span className="text-xs text-secondary">
                                    {formatEventTime(event.timestamp)}
                                  </span>
                                </div>
                                <p className="text-xs text-secondary/70">
                                  {t('bookingEmails.via')} {event.provider}
                                  {event.message_id && (
                                    <span className="ml-md font-mono">
                                      ID: {event.message_id.substring(0, 12)}...
                                    </span>
                                  )}
                                </p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
